import type { ReactNode } from 'react';
import type { Meal, MealItem } from '../api/types';
import { formatTime } from '../format/dates';
import { formatEstimateKcal, formatKcal, mealTypeLabel } from '../format/nutrition';
import { ForwardIcon } from './icons';
import { FotoConEspera } from './FotoConEspera';
import { ItemRow } from './ItemRow';
import { useSignedImageUrl } from './MediaThumb';

/** El total de una comida a partir de sus ítems, cuando el resumen del día no lo trajo. */
function totalOf(items: MealItem[]): { low: number; high: number } {
  return items.reduce(
    (accumulated, item) => {
      const estimate = item.currentEstimate;
      if (estimate === null || !item.countsTowardsTotals) {
        return accumulated;
      }

      return {
        low: accumulated.low + (estimate.kcalLow ?? estimate.kcal),
        high: accumulated.high + (estimate.kcalHigh ?? estimate.kcal),
      };
    },
    { low: 0, high: 0 },
  );
}

/**
 * Una comida: cabecera, la foto si la hay, y una fila por ítem.
 *
 * La foto va a ancho completo y no como miniatura porque la fotografía del usuario es el
 * contenido dominante de esta pantalla: la interfaz es el marco, no el protagonista.
 */
export function MealCard({
  meal,
  totalLow,
  totalHigh,
  onOpenItem,
  onOpenMeal,
  footer,
}: {
  meal: Meal;
  totalLow?: number;
  totalHigh?: number;
  onOpenItem: (itemId: string) => void;
  onOpenMeal?: () => void;
  footer?: ReactNode;
}) {
  const fallback = totalOf(meal.items);
  const low = totalLow ?? fallback.low;
  const high = totalHigh ?? fallback.high;

  /*
   * La foto de la comida, con el plato primero y el rótulo como respaldo.
   *
   * Antes sólo se aceptaba `Dish`, y eso hacía que una comida resuelta por rótulo —sacaste la
   * foto del envase, que es un camino de primera clase (RF-10)— apareciera sin ninguna imagen.
   * La persona sacó una foto y la tarjeta no la mostraba: para ella la aplicación se comió la
   * foto.
   */
  const conFoto = meal.items.find((item) => item.media.some((media) => media.role === 'Dish'))
    ?? meal.items.find((item) => item.media.length > 0);
  const heroMedia =
    conFoto?.media.find((media) => media.role === 'Dish') ?? conFoto?.media[0] ?? null;
  const heroUrl = useSignedImageUrl(meal.id, conFoto?.id ?? '', heroMedia);

  return (
    <article className="meal-card">
      {/*
        La cabecera abre la comida, y ésa es la única puerta que había que abrir.
        Borrar una comida ya existía (RF-14) pero vivía en una pantalla a la que sólo se llegaba
        desde Historial: quien acababa de subir algo mal —que es cuando uno quiere borrarlo— lo
        tenía adelante en «Hoy» y no había manera de tocarlo. Tocar una fila lleva al ítem; tocar
        el encabezado, a la comida entera. Cada nivel se abre desde donde se lo nombra.
      */}
      <MealHeader onOpen={onOpenMeal}>
        <span>
          <span className="meal-type">{mealTypeLabel(meal.type)}</span>
          <span className="meal-time">{formatTime(meal.occurredAt)}</span>
        </span>
        <span className={low === high ? 'item-kcal' : 'item-kcal uncertain'}>
          {low === high ? formatKcal(low) : `${formatKcal(low)}–${formatKcal(high)}`} kcal
        </span>
      </MealHeader>

      {/*
        Una sola pieza para las dos esperas: la URL firmada y la descarga de la imagen. Antes el
        esqueleto cubría sólo la primera, y la segunda —que es la larga, cientos de kilobytes
        contra un objeto remoto— dejaba un hueco en blanco del tamaño exacto de la foto.
      */}
      {heroMedia !== null && (
        <FotoConEspera
          url={heroUrl}
          alt={`Foto de ${mealTypeLabel(meal.type)}`}
          className="meal-photo"
        />
      )}

      {meal.items.map((item) => (
        <ItemRow key={item.id} mealId={meal.id} item={item} onOpen={() => onOpenItem(item.id)} />
      ))}

      {footer}
    </article>
  );
}

/** El valor de un ítem tal como lo muestra una fila, para reusarlo en textos de botones. */
export function itemKcalLabel(item: MealItem): string {
  return item.currentEstimate === null ? '—' : formatEstimateKcal(item.currentEstimate);
}

/**
 * El encabezado de la comida: botón cuando hay a dónde ir, encabezado a secas cuando no.
 *
 * No se usa un botón siempre porque un control que no hace nada es peor que ninguno: la persona
 * lo toca, no pasa nada, y aprende a no confiar en la pantalla. En el detalle de la comida ya
 * estás adentro, así que ahí no hay a dónde abrir.
 */
function MealHeader({ onOpen, children }: { onOpen?: () => void; children: ReactNode }) {
  if (onOpen === undefined) {
    return <header className="meal-head">{children}</header>;
  }

  return (
    <button type="button" className="meal-head meal-head-open" onClick={onOpen}>
      {children}
      <ForwardIcon size={15} />
    </button>
  );
}
